/**
 * Product thumbnail with store-logo fallback when image is missing or fails to load.
 */
import { useEffect, useState } from 'react'
import { getProductImageSrc } from '@/lib/productImage'
import { cn } from '@/lib/utils'
import type { ProductImage } from '@/types'

interface ProductThumbnailProps {
  product: { name?: string; images?: ProductImage[] } | null | undefined
  fallbackSrc: string
  alt?: string
  className?: string
}

export function ProductThumbnail({
  product,
  fallbackSrc,
  alt,
  className,
}: ProductThumbnailProps) {
  const primarySrc = getProductImageSrc(product)
  const [src, setSrc] = useState(primarySrc ?? fallbackSrc)
  const isFallback = !primarySrc || src === fallbackSrc

  useEffect(() => {
    setSrc(getProductImageSrc(product) ?? fallbackSrc)
  }, [product, fallbackSrc])

  return (
    <div className={cn('overflow-hidden', className)}>
      <img
        src={src}
        alt={alt ?? product?.name ?? 'Product'}
        className={cn(
          'h-full w-full',
          isFallback ? 'bg-gray-50 object-contain p-2' : 'object-cover',
        )}
        onError={() => {
          if (src !== fallbackSrc) {
            setSrc(fallbackSrc)
          }
        }}
      />
    </div>
  )
}
